import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Ansel, the Ash Scholar (models/ansel.ts, docs/ANTIHEROES.md): on the fallen sentinel's bones like Ser Corvin. His
 * own: the shot (the staff thrust at the target, the fire leaving it at 0.13 s), the ember (the book raised and blown
 * on), the ash wall (the staff swept across before him), the channel of the library (book and staff raised high,
 * trembling) and the library itself (the staff driven down into the ground).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on the
 * trunk leans it forward, +Y turns the trunk to its left. The staff hangs from the `sword` bone, the book from `shield`.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** The staff drawn back by the shoulder. */
const BACK: P = { chest: [-0.05, -0.25, 0], armR: [-0.6, 0, -0.3], foreR: [-1.2, 0, 0], sword: [0.6, 0, 0], legR: [0.2, 0, 0.05], legL: [-0.15, 0, 0] };
/** Thrust: the staff's crown pointed at the target. */
const THRUST: P = { root: [0, 0, 0, 0, -0.02, 0.06], chest: [0.12, 0.25, 0], head: [0.05, -0.15, 0], armR: [-1.45, 0, 0.1], foreR: [-0.1, 0, 0], sword: [0.1, 0, 0], armL: [0.2, 0, 0.15], legL: [-0.35, 0, 0], shinL: [0.3, 0, 0], legR: [0.25, 0, 0] };

const attack = (): Clip<B> =>
  clip<B>('attack', 1.1, false, (b, t) => {
    timeline(b, t, [
      [0, BACK],
      [0.13, THRUST, outExpo],
      [0.45, THRUST, outCubic],
      [1.1, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.015 * kick(t, 0.13, 6, 9));
  }, undefined, 'Disparo de fuego');

/** The ember: the book raised before the face and blown on, the staff low. */
const ember = (): Clip<B> =>
  clip<B>('ember', 1.3, false, (b, t) => {
    const up: P = { chest: [-0.08, 0.2, 0], head: [0.15, 0.1, 0], armL: [-1.5, 0, 0.3], foreL: [-0.6, 0, 0], shield: [0.3, 0, 0] };
    const blow: P = { ...up, chest: [0.12, 0.25, 0], head: [0.3, 0.1, 0], armL: [-1.7, 0, 0.1], foreL: [-0.2, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.35, up, outCubic],
      [0.5, blow, outExpo],
      [0.9, blow],
      [1.3, {}, inOut],
    ], { head: 0.05 });
  }, undefined, 'Brasa errante');

/** The ash wall: the staff swept across in front of him, from his right to his left. */
const wall = (): Clip<B> =>
  clip<B>('wall', 1.4, false, (b, t) => {
    const right: P = { chest: [0, -0.5, 0], body: [0, -0.2, 0], armR: [-1.4, 0, -0.9], foreR: [-0.2, 0, 0], sword: [0.4, 0, 0], legR: [0.15, 0, 0] };
    const left: P = { chest: [0.05, 0.55, 0], body: [0, 0.25, 0], armR: [-1.4, 0, 0.6], foreR: [-0.2, 0, 0], sword: [0.4, 0, 0], legL: [-0.3, 0, 0], shinL: [0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.3, right, outQuad],
      [0.6, left, inCubic],
      [0.95, left],
      [1.4, {}, inOut],
    ], { head: 0.06, flapF: 0.1, flapB: 0.1 });
  }, undefined, 'Muro de ceniza');

/** The library burns, channelled: book and staff raised high, his whole frame trembling with it. */
const HIGH: P = {
  root: [0, 0, 0, 0, -0.04, 0],
  body: [-0.1, 0, 0],
  chest: [-0.3, 0, 0],
  head: [-0.45, 0, 0],
  armL: [-2.6, 0, 0.35],
  foreL: [-0.3, 0, 0],
  shield: [0.6, 0, 0],
  armR: [-2.8, 0, -0.2],
  foreR: [-0.1, 0, 0],
  sword: [2.7, 0, 0],
  legL: [-0.2, 0, -0.1],
  shinL: [0.25, 0, 0],
  legR: [0.2, 0, 0.1],
  shinR: [0.3, 0, 0],
};
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.03;
    for (const [k, v] of Object.entries(HIGH) as [B, readonly number[]][]) b[k].rot((v[0] ?? 0) + (k === 'armR' || k === 'chest' ? shake : 0), v[1] ?? 0, v[2] ?? 0);
    b.root.pos(0, -0.04 + 0.01 * Math.sin(p * 2), 0);
    b.flapF.rot(0.1 * Math.sin(p * 3));
    b.flapB.rot(-0.12 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.006;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The library lands: the staff brought down from overhead and driven into the ground. */
const library = (): Clip<B> =>
  clip<B>('library', 1.6, false, (b, t) => {
    const down: P = { root: [0, 0, 0, 0, -0.18, 0.15], body: [0.3, 0, 0], chest: [0.35, 0, 0], head: [0.15, 0, 0], armR: [-0.9, 0, 0.05], foreR: [-0.05, 0, 0], sword: [0.9, 0, 0], armL: [-0.6, 0, 0.5], shield: [0.3, 0, 0], legL: [-0.7, 0, 0], shinL: [0.9, 0, 0], legR: [0.45, 0, 0], shinR: [0.5, 0, 0], flapF: [-0.4, 0, 0], flapB: [0.4, 0, 0] };
    timeline(b, t, [
      [0, HIGH],
      [0.18, down, inCubic],
      [0.8, down],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0.02 * kick(t, 0.18, 7, 6), 0, 0);
  }, undefined, 'La biblioteca arde');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with his own shot and powers in place of the bash. */
export const anselClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), ember(), wall(), channel(), library()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { ansel: anselClips };
